// Canonical English string catalog — the source of truth every other
// locale file is translated from and the fallback t() reaches for when a
// translated catalog is missing a key (see src/i18n/index.js). Keep this
// file's shape (nesting + key names) exactly mirrored in every other
// locale file; a translation script/reviewer diffs key sets against this
// one.
export default {
  common: {
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    back: 'Back',
    retry: 'Retry',
    edit: 'Edit',
    editField: 'Edit {label}',
    add: 'Add',
    checking: 'Checking…',
    address: 'Address'
  },
  header: {
    statusOnline: 'Status: online',
    statusOffline: 'Status: offline',
    switchProfile: 'Switch profile',
    testnetBadge: 'TESTNET',
    updateAvailable: 'New version available',
    refresh: 'Refresh',
    workerUnavailable: 'Wallet features unavailable — worker failed to start',
    lockWallet: 'Lock wallet'
  },
  tabs: {
    home: 'Home',
    receive: 'Receive',
    send: 'Send',
    activity: 'Activity',
    settings: 'Settings'
  },
  // Desktop-only sidebar/new-window strings (feature-ui.md's Desktop
  // section, added 2026-07-28). Only this English catalog has these keys so
  // far — t() falls back to English for every other locale until a
  // follow-up translation pass adds them there too (see DECISIONS.md).
  nav: {
    collapseSidebar: 'Collapse sidebar',
    expandSidebar: 'Expand sidebar',
    openInNewWindow: 'Open in new window'
  },
  landing: {
    welcomeBack: 'Welcome back',
    welcomeBackName: 'Welcome back, {name}',
    unlockWithBiometrics: 'Unlock with biometrics',
    orEnterPasscode: 'or enter your passcode',
    enterPasscode: 'Enter your passcode',
    passcodePlaceholder: '6-digit code',
    unlock: 'Unlock',
    unlockLockedOut: 'Try again in {seconds}s',
    back: 'Back',
    chooseLanguage: 'Choose language',
    heroCopy: "Stablecoin treasury, simplified. Hold, receive, exchange and move your company's digital dollars like a bank account.",
    instantAccountOpening: 'Instant account opening',
    // Full 7-section FAQ from Tynti_FAQ_20260811.md (superseding the
    // previous curated subset — see git history for that version and
    // DECISIONS.md 2026-08-11 for why it was curated in the first place;
    // this pass reverses that call at the user's explicit request). Order
    // is re-sequenced from the source doc's own section order to the order
    // a Treasury Officer evaluating this tool would actually want to read
    // it in: what this is, then operational controls (can my team use this
    // safely), then day-to-day compliance tooling, then legal/regulatory
    // standing, then the technical security standards backing it, and only
    // then the introductory/pitch material (stablecoin 101, why it's
    // faster/cheaper) and mechanical details — someone who reached this
    // FAQ already knows they're evaluating a treasury tool, not being sold
    // on stablecoins for the first time. This still renders on both the
    // landing screen and the lock screen (renderWelcomeBack), so it's long
    // — that's an accepted trade-off of answering the request in full
    // rather than re-curating a second time.
    //
    // The source doc's ~35-bullet "Security Standards" section is
    // deliberately NOT reproduced bullet-for-bullet here — condensed to one
    // terse Q&A per category with a link to the named external standard
    // instead (per explicit request), since the standards themselves are
    // the authoritative, current source for their own requirements; this
    // FAQ points at them rather than re-narrating them and risking drift.
    // Answers avoid the Pre/Bold/Body1/Body2 fragment-splitting pattern the
    // three pre-existing answers below (faqDifferentTitle, faqMistakeTitle)
    // still use — that pattern breaks translation in languages where the
    // emphasized phrase doesn't sit where English puts it (see
    // DECISIONS.md); every new answer here is one plain string per
    // paragraph/bullet instead, with `strong`/link markup applied in
    // landing.js's faq(), not baked into the translated string.
    faqIntro: "We lead with radical honesty. Self-custody isn't magic and it isn't a bank account with better branding — it's a genuine trade-off. Here's what that means, and how Tynti gives you bank-like controls without ever being able to touch, freeze, or lose your money for you.",

    faqSectionOverview: 'Overview',
    faqWhatIsTitle: 'What is Tynti?',
    faqWhatIsAnswer: "Tynti is a self-custody treasury management tool for stablecoins. It lets businesses hold, receive, exchange, and move stablecoins across supported chains (Tron, Ethereum) directly from a web browser — no app-store download required. Tynti runs entirely client-side: there's no Tynti server holding your money, and no Tynti database that could be hacked, subpoenaed, or shut down.",

    faqSectionControls: 'Controls & Operations',
    faqStaffTitle: 'Can my staff initiate payments without being able to steal the funds?',
    faqStaffLead: 'Yes. We replace the "hardware wallet in a home safe" model with software-enforced roles and maker-checker approvals:',
    faqStaffBullet1Label: 'Owner supremacy',
    faqStaffBullet1Text: ' — you retain root control, hold the ultimate signing keys, and approve large transactions from your own phone.',
    faqStaffBullet2Label: 'Clerk / Preparer roles',
    faqStaffBullet2Text: " — staff can initiate payments, link invoices, and manage recipients, but can't move funds above your set thresholds without your cryptographic sign-off.",
    faqStaffBullet3Label: 'View-only access',
    faqStaffBullet3Text: ' — your accountant or auditor can see everything without ever being able to move a cent.',
    faqStaffBullet4Label: 'Multi-sig security',
    faqStaffBullet4Text: ' — transactions can require 2-of-2 or 2-of-3 signatures, so no single compromised device or person can drain the account.',
    faqStaffBullet5Label: 'Spending limits and approval thresholds',
    faqStaffBullet5Text: " — set per user, so a payment to a new supplier gets a second look before it's live.",
    faqAddressTitle: "How do I make sure we don't send money to the wrong address?",
    faqAddressAnswer: "Our Address Safety Suite exists specifically because blockchain transactions can't be reversed once confirmed. It enforces allowlisted address books, restricts free-text address entry at send time, warns you loudly the first time you pay a new, unseen address, and supports first-payment verification (a small test transfer) before you send the full amount.",
    faqSeesTitle: 'What does Tynti actually see or control?',
    faqSeesAnswer: 'Private keys and seed phrases are generated and stored on your device — inside a Web Worker, never transmitted to us. Tynti cannot see your keys, initiate a transaction on your behalf, or reverse one you\'ve sent. If you\'re ever unsure whether something is "Tynti-controlled" or "you-controlled," the answer is almost always the latter.',
    faqMistakeTitle: 'What happens if I lose my keys or make a mistake?',
    faqMistakeIntroPre: "This is the most important question on this page, so we'll answer it plainly: ",
    faqMistakeIntroBold: 'there is no deposit insurance, no recovery hotline, and no chargebacks.',
    faqMistakeIntroPost: ' If your private keys are lost and no backup exists, the funds are not recoverable — not by Tynti, not by anyone. You are acting as your own bank, with all the responsibility that implies.',
    faqMistakeListLead: 'What we do to make that manageable:',
    faqMistakeBullet1Label: 'Guided backup ceremonies',
    faqMistakeBullet1Text: ' at setup, walking you step by step through securing your recovery phrase, so "I forgot to back it up" isn\'t a trap you fall into by accident.',
    faqMistakeBullet2Label: 'Multi-device and role-based recovery options',
    faqMistakeBullet2Text: ', so your business isn\'t a single laptop away from losing access — recovery doesn\'t have to mean "one device, one point of failure."',
    faqMistakeBullet3Label: 'A clear, upfront explanation',
    faqMistakeBullet3Text: ' of what you own and what that ownership means, before you rely on it — not buried in terms and conditions.',
    faqMistakeClosing: "We won't pretend this is as forgiving as a bank's \"forgot my password\" flow. It isn't. But it's also not a decision you have to make with no support — we tell you exactly what to do and check that you've done it.",
    faqDisappearTitle: 'What happens if Tynti disappears?',
    faqDisappearAnswer: 'Because Tynti never holds your funds, this is a much smaller problem than it sounds. Your money isn\'t sitting in a "Tynti account" that could vanish with the company — it\'s sitting on public blockchains under keys that only you control. If Tynti ceased to operate tomorrow, your recovery phrase and exported keys work in any standard-compatible wallet, with or without us.',

    faqSectionCompliance: 'Daily Operations & Compliance',
    faqEvidenceTitle: 'How do I handle compliance and source-of-funds checks with my bank?',
    faqEvidenceAnswer: 'For global traders, evidence is the product. Generate a one-click Evidence Pack for any transaction: a PDF dossier with the attached commercial invoice, the on-chain transaction hash, counterparty profile details, and the reporting-currency value stamped at the exact time of receipt — built to survive bank compliance reviews and auditor scrutiny.',
    faqInvoiceTitle: 'Does Tynti integrate with my invoices?',
    faqInvoiceAnswer: "Yes — Invoice-Linked Reconciliation. Payment requests carry invoice references, and inbound transfers automatically match to the open request. Unmatched inbound funds are flagged for quick manual linking, replacing spreadsheets and chat threads as your payment-matching engine.",
    faqSegregateTitle: 'Can I segregate my funds for different purposes?',
    faqSegregateAnswer: "Yes. Create multi-account segregation for your working float, operational expenses, and tax reserves — each with its own policies, spending limits, and approval thresholds, the same way you'd separate accounts at a bank.",
    faqWhatsAppTitle: 'How do I share payment details with my buyers or suppliers?',
    faqWhatsAppAnswer: 'Generate payment request links, address cards, and "payment received" receipts formatted for WhatsApp forwarding. Your counterparties don\'t need to log in or understand blockchains to view or verify a payment page.',
    faqExportTitle: 'Can I export data for my accountant?',
    faqExportAnswer: "Yes. CSV/Excel export includes dates, counterparties, references, and fiat values at time of transaction, alongside the audit trail of who initiated and who approved each payment — exportable for Xero, QuickBooks, or your accountant's own format.",

    faqSectionTrust: 'Trust & Regulation',
    faqRegulatedTitle: 'Is Tynti regulated?',
    faqRegulatedAnswerPre: "No, Tynti is not a regulated entity, and is completely exempt from the licensing requirements of the EU's Markets in Crypto-Assets Regulation (MiCA).",
    faqRegulatedAnswerPost: 'Tynti is supplied as a pure self-custody software wallet interface. Under Recital 11 of the MiCA regulation, the framework explicitly excludes providers that only supply software or hardware tools and do not hold crypto-assets or private keys on behalf of clients.',
    faqKycTitle: 'Do I need to complete KYC to use Tynti?',
    faqKycAnswer: "No. There's no KYC wall and no compliance queue standing between you and your own money. That said, when you deal with regulated counterparties — your bank, an auditor, a supplier's compliance team — you'll still need to show your own paperwork to them. Tynti removes friction on our side; it doesn't (and can't) remove it everywhere in the trade you're part of.",
    faqFreezeTitle: 'Can Tynti freeze my account or shut me down?',
    faqFreezeAnswer: "No — and this isn't a policy promise, it's a structural fact. Tynti has no account to close, no balance to freeze, and no platform gate to put you behind. There's simply no mechanism for Tynti, or anyone claiming to be Tynti, to de-platform you, reject your account, or hold your funds hostage during a review.",
    faqSafeTitle: "How do I know the software is safe if there's no regulated entity standing behind it?",
    faqSafeAnswer: "Trust here isn't a brand promise — it's auditable, open-source code. Anyone, including your own security team, can inspect exactly how Tynti handles your keys and your transactions. There's no closed-source backend to take our word for, and no hidden server that could be hacked or quietly shut down, because there isn't one.",

    faqSectionStandards: 'Security Standards',
    faqStandardsTitle: 'What security standards does Tynti follow?',
    faqStandardsIntro: "These are controls we build to and can show evidence for — not third-party certifications; no formal certification scheme yet exists for self-custody wallets like this.",
    faqStandardsBullet1Label: 'Application security',
    faqStandardsBullet1Text: 'PBKDF2-hashed PINs, WebAuthn/passkey support, increasing lockouts, AES-256-GCM encryption at rest, and a strict content security policy, targeting',
    faqStandardsBullet1Link: 'OWASP ASVS Level 2',
    faqStandardsBullet2Label: 'Key & wallet handling',
    faqStandardsBullet2Text: 'Keys generated only via an audited wallet library, a guided backup-verification ceremony, and no analytics or telemetry that can see a wallet address, balance, or transaction, aligned with the',
    faqStandardsBullet2Link: 'CryptoCurrency Security Standard (CCSS)',
    faqStandardsBullet3Label: 'Address & transaction correctness',
    faqStandardsBullet3Text: 'Standard derivation paths, checksum-validated addresses, and no possible cross-chain misdirected sends — per',
    faqStandardsBullet3Link1: 'BIP-39/32/44',
    faqStandardsBullet3Link2: 'EIP-55',
    faqStandardsBullet3After: ', and TRC-20.',
    faqStandardsBullet4Label: 'Supply chain',
    faqStandardsBullet4Text: 'Minimal, justified dependencies, blocking vulnerability and secret scans before every release, and no third-party CDN at runtime.',

    faqSectionBackground: 'Background',
    faqStablecoinTitle: 'What is a stablecoin, in plain English?',
    faqStablecoinAnswer: 'A stablecoin is a digital form of money — each unit is designed to hold a steady value, usually pegged 1:1 to a currency like the US dollar or euro. Because it lives on a public blockchain rather than inside one bank\'s ledger, it can move across the internet as easily as sending an email — no branch hours, no correspondent banks, no "please allow 3-5 business days." Tynti is the wallet that lets you hold, send, and receive stablecoins (USDC, USDT, EURC, EURT, CNHT) without needing to understand the plumbing underneath.',
    faqDifferentTitle: 'How is Tynti different from a bank or a crypto exchange?',
    faqDifferentLeadPre: 'Radical honesty: ',
    faqDifferentLeadBold: 'your keys, your books, your software.',
    faqDifferentBody1: 'A bank or exchange holds your money on your behalf — which means it can also freeze it, delay it, or reject you as a customer. Tynti never takes custody of your funds. There is no Tynti account to close, no balance sitting on our books, no platform in the middle that can say no. You skip the KYC queue and take direct, permanent ownership of your treasury.',
    faqDifferentBody2: "The honest flip side: because no one is holding your funds for you, no one can rescue you from a mistake either. We explain exactly what that means in the Controls & Operations section above — please read it before you move real money.",
    faqWhyTitle: 'Why is this faster, cheaper, and safer than what I use today?',
    faqWhyBullet1Label: 'Faster:',
    faqWhyBullet1Text: 'Stablecoin transfers settle 24/7, including nights, weekends, and holidays — typically in seconds, not "1-3 business days."',
    faqWhyBullet2Label: 'Cheaper:',
    faqWhyBullet2Text: "There are no platform fees. Tynti offers gas-less transactions, so you're not paying network fees or holding volatile crypto just to move stablecoins.",
    faqWhyBullet3Label: 'Safer from institutional risk:',
    faqWhyBullet3Text: "There's no single bank to have a liquidity problem, no correspondent-banking chain to break, and no account that can be put on hold while someone reviews it. Your funds aren't sitting on anyone's balance sheet but your own.",
    faqWhyNote: '"Safer" here means safer from third-party failure and gatekeeping — not risk-free. The Controls & Operations section above covers what self-custody does not protect you from.',
    faqFeesTitle: 'What are the fees?',
    faqFeesAnswer: 'There are no platform fees and no subscription costs. You only pay if you choose to purchase dedicated, optional human support.',

    faqSectionTechnical: 'Technical Details',
    faqAppTitle: 'Do I need to download an app?',
    faqAppAnswer: 'No. Tynti is a Progressive Web App (PWA). Open the site in your browser and it runs natively on your device — no App Store, no Google Play, no extension to install.',
    faqFiatTitle: 'Does Tynti support fiat conversions (off-ramps)?',
    faqFiatAnswer: 'No. Tynti is a pure stablecoin treasury tool built to help you settle directly with suppliers and buyers without the cost leakage of fiat-to-crypto conversion. OTC and off-ramp relationships stay in your hands — Tynti focuses entirely on making stablecoin settlement faster, cheaper, and safely auditable end to end.',
    namePlaceholder: 'Your name / company name',
    emailPlaceholder: 'Email address (optional)',
    anonymous: 'Anonymous',
    anonName: 'Anon',
    codePlaceholder: '6-digit passcode',
    codeConfirmPlaceholder: 'Repeat passcode',
    agreeTermsPre: 'I agree to the ',
    agreeTermsLink: 'MIT license',
    agreeTermsPost: ' to use this software',
    agreeSelfCustodyPre: 'I understand this is a self-custody solution, provided ',
    agreeSelfCustodyLink: '"AS IS"',
    agreeSelfCustodyPost: ', used at my own risk.',
    openYourAccount: 'Open your account',
    opening: 'Opening…',
    openAccount: 'Open Account'
  },
  home: {
    totalBalance: 'Total balance ({currency})',
    balanceInfoTitle: 'What is this?',
    balanceInfo: 'This is the combined value of every account below, converted into your reporting currency ({currency}) so accounts holding different stablecoins can be added together. Change the reporting currency in Settings.',
    accounts: 'Accounts',
    sortByName: 'Sort by name',
    sortByBalance: 'Sort by balance',
    recentActivity: 'Recent activity',
    activityInfo: 'These are the actual transactions recorded on the blockchain for the accounts above — not just entries in an app database.',
    noActivity: 'No activity yet.',
    updating: ' · updating…',
    addressOnChain: '{ccy} address on {chain}',
    noSendLimit: 'No send limit set',
    sendLimit: 'Send limit: {limit}'
  },
  receive: {
    title: 'Receive',
    noAccountsEmpty: 'Open an account first to receive funds.',
    openAccount: 'Open account',
    receivingAccount: 'Receiving account',
    receivingCurrency: 'Receiving currency',
    showDetails: 'Show account details',
    hideDetails: 'Hide account details',
    network: 'Network',
    generatingAddress: 'Generating address…',
    copyAddress: 'Copy address',
    qrAlt: 'QR code for receiving address {address}',
    // feature-outstanding.md (2026-08-07): gasless-capable currencies
    // (GasFree on Tron, Pimlico's paymaster on Ethereum — see store.js's
    // isGaslessCapable) resolve to a genuinely different on-chain address
    // than a plain account (a GasFree contract address / ERC-4337 smart
    // account, not the wallet's plain EOA) — this explains why, right where
    // that address is shown, rather than leaving it unexplained.
    gaslessNote: 'Gasless address — sending {ccy} from here never needs {native}, the fee is paid automatically in {ccy} itself.'
  },
  send: {
    title: 'Send',
    noAccountsEmpty: 'Open an account first to send funds.',
    openAccount: 'Open account',
    fromAccount: 'From account',
    to: 'To',
    selectDestination: 'Select destination…',
    newRecipient: '+ New recipient…',
    editRecipient: 'Edit this recipient',
    manageRecipients: 'Manage recipients',
    amount: 'Amount',
    available: 'Available: {amount} · {network}',
    sendLimit: 'Account send limit: {limit}',
    overrideLimit: 'Override with re-authentication',
    overrideNewRecipient: 'Override with re-authentication',
    memo: 'Memo (optional)',
    memoPlaceholder: 'Reference or note',
    // Currency-conversion "deliver as" (feature-swap.md) — English-only
    // until a follow-up translation pass, same precedent as
    // reviewPlaceholder below (see DECISIONS.md).
    deliverAs: 'Deliver as',
    deliverAsSame: 'Same as sent',
    deliverAsUnavailable: 'Not available for this currency/network — the recipient will receive what you send.',
    continueToReview: 'Continue to review',
    reviewTitle: 'Review transfer',
    reviewFrom: 'From',
    reviewTo: 'To',
    reviewAmount: 'Amount',
    reviewNetwork: 'Network',
    reviewMemo: 'Memo',
    reviewFee: 'Est. network fee',
    reviewApprovalFee: 'Est. approval fee',
    reviewRecipientReceives: 'Recipient receives',
    swapMultiStepNote: 'This may involve more than one on-chain step (an approval, then the swap) and can take longer than a plain transfer. If the price moves before you confirm, this will be refused rather than executed at a worse rate. Not every currency pair has an available route — if none is found, you\'ll see an error here rather than a quote.',
    swapQuoteStale: 'The exchange rate moved — go back and try again',
    reviewFeeLoading: 'Estimating…',
    reviewFeeUnavailable: 'Unavailable — try again',
    // feature-outstanding.md (2026-08-07): a gasless fee is paid in the
    // same stablecoin being sent, not ETH/TRX — shown as a small note under
    // the fee row so "1.3 USDT" doesn't read as a typo for a gas fee.
    reviewFeeGasless: 'Gasless — no {native} needed, paid in {ccy}',
    // Gasless only: amount + fee together exceed the real balance, since
    // both come out of the same token (see actions.js's fetchReviewFee).
    reviewFeeInsufficientForFee: 'Not enough {ccy} to also cover the {fee} {ccy} fee — reduce the amount or add funds',
    reviewArrival: 'Est. arrival',
    reviewArrivalOnChain: 'Once confirmed on-chain',
    mainnetAckLabel: 'I understand this sends real funds on Mainnet.',
    reviewSendLimit: 'Account send limit',
    reviewLimitOverridden: 'overridden via re-authentication',
    reviewNewRecipient: 'First payment to a new recipient',
    reviewNewRecipientNotice: 'Double-check this address carefully',
    reviewNewRecipientOverridden: 'overridden via re-authentication',
    back: 'Back',
    sending: 'Sending…',
    confirmTransfer: 'Confirm transfer',
    successTitle: 'Transfer complete',
    successSub: '{amount} sent successfully',
    successSwapOutcome: 'Recipient received {amount}',
    ref: 'Ref {id}',
    hash: 'Hash {hash}',
    // Gasless Tron sends (GasFree relay) broadcast asynchronously — what
    // comes back immediately is the relay's own submission id, not a real
    // Tron txHash, so there's no working explorer link to show yet (see
    // send.js's renderSuccess and DECISIONS.md's GasFree hash entry).
    // Activity resolves the real hash independently once TronGrid indexes
    // the transaction.
    hashPending: 'Submitted — the on-chain hash will appear in Activity once confirmed',
    done: 'Done',
    // Desktop split-pane's right-hand placeholder before the form is
    // continued to review (feature-ui.md's Desktop section) — English-only
    // until a follow-up translation pass, see DECISIONS.md.
    reviewPlaceholder: 'Your transfer review will appear here once you continue.',
    formLockedWhileReviewing: 'Editing is locked while you’re reviewing — go Back to make changes.'
  },
  transfer: {
    title: 'Transfer',
    noAccountsEmpty: 'Open an account first to transfer funds.',
    openAccount: 'Open account',
    to: 'To',
    selectDestination: 'Select destination…',
    addAccount: '+ Add account',
    amount: 'Amount',
    available: 'Available: {amount}',
    sendLimit: 'Account send limit: {limit}',
    overrideLimit: 'Override with re-authentication',
    chain: 'Chain',
    estFee: 'Est. network fee: {amount}',
    // Currency-conversion "deliver as" (feature-swap.md) — see send's
    // identical keys/comment.
    deliverAs: 'Deliver as',
    deliverAsSame: 'Same as sent',
    deliverAsUnavailable: 'Not available for this currency/network — the recipient will receive what you send.',
    continueToReview: 'Continue to review',
    reviewTitle: 'Review transfer',
    reviewFrom: 'From',
    reviewTo: 'To',
    reviewAmount: 'Amount',
    reviewChain: 'Chain',
    reviewFee: 'Est. network fee',
    reviewApprovalFee: 'Est. approval fee',
    reviewRecipientReceives: 'Recipient receives',
    swapMultiStepNote: 'This may involve more than one on-chain step (an approval, then the swap) and can take longer than a plain transfer. If the price moves before you confirm, this will be refused rather than executed at a worse rate. Not every currency pair has an available route — if none is found, you\'ll see an error here rather than a quote.',
    swapQuoteStale: 'The exchange rate moved — go back and try again',
    reviewFeeLoading: 'Estimating…',
    reviewFeeUnavailable: 'Unavailable — try again',
    reviewFeeGasless: 'Gasless — no {native} needed, paid in {ccy}',
    reviewFeeInsufficientForFee: 'Not enough {ccy} to also cover the {fee} {ccy} fee — reduce the amount or add funds',
    mainnetAckLabel: 'I understand this sends real funds on Mainnet.',
    reviewSendLimit: 'Account send limit',
    reviewLimitOverridden: 'overridden via re-authentication',
    back: 'Back',
    sending: 'Sending…',
    confirmTransfer: 'Confirm transfer',
    successTitle: 'Transfer complete',
    successSub: '{amount} moved successfully',
    successSwapOutcome: 'Recipient received {amount}',
    ref: 'Ref {id}',
    hash: 'Hash {hash}',
    // See send.hashPending's comment — same GasFree async-submission-id
    // situation, shared here since Transfer uses the same signTransfer path.
    hashPending: 'Submitted — the on-chain hash will appear in Activity once confirmed',
    done: 'Done'
  },
  activity: {
    title: 'Activity',
    pdf: 'PDF',
    csv: 'CSV',
    searchPlaceholder: 'Search description, account, or hash…',
    allAccounts: 'All accounts',
    sortNewest: 'Newest first',
    sortOldest: 'Oldest first',
    sortAmountHighLow: 'Amount (high–low)',
    sortAmountLowHigh: 'Amount (low–high)',
    filterAll: 'All',
    filterIn: 'In',
    filterOut: 'Out',
    filterAllTime: 'All time',
    filter1M: '1M',
    filter12M: '12M',
    noMatches: 'No transactions match these filters.',
    noActivity: 'No activity yet.',
    viewOnChain: 'View on chain ↗',
    fxRate: 'FX rate: 1 {ccy} = {rate} {currency}',
    hash: 'Hash {hash}',
    sentTo: 'Sent to {counterparty}',
    receivedFrom: 'Received from {counterparty}',
    addTagPlaceholder: 'Add tag…',
    addTag: 'Add',
    removeTag: 'Remove tag {tag}',
    // Desktop dense-table column headers (feature-ui.md's Desktop
    // section) — English-only until a follow-up translation pass, see
    // DECISIONS.md.
    colDate: 'Date',
    colDescription: 'Description',
    colAccount: 'Account',
    colAmount: 'Amount'
  },
  recipients: {
    title: 'Recipients',
    add: '+ Add',
    empty: 'No saved recipients yet. Add one from here or from the Send screen.',
    editAria: 'Edit {name}',
    deleteAria: 'Delete {name}'
  },
  settings: {
    title: 'Settings',
    profile: 'Profile',
    name: 'Name',
    email: 'Email',
    avatar: 'Avatar',
    reportingCurrency: 'Reporting currency',
    appearance: 'Appearance',
    matchDevice: 'Match device',
    light: 'Light',
    dark: 'Dark',
    language: 'Language',
    defaultStablecoin: 'Default stablecoin',
    defaultChain: 'Default chain',
    testnet: 'Testnet',
    autoLock: 'Auto-lock after inactivity',
    autoLockMinutes: '{minutes} min',
    wipeAfter: 'Wipe after failed attempts',
    wipeAfterHelp: 'Off by default. If enabled, this device permanently deletes this account\'s local data (recovery phrase, recipients, accounts) after the chosen number of consecutive wrong passcode attempts. Only do this if you have your recovery phrase backed up elsewhere.',
    wipeAfterOff: 'Off',
    wipeAfterN: '{n} attempts',
    llmsTxt: 'llms.txt',
    downloadLlmsTxt: 'Download llms.txt',
    copyLlmsTxt: 'Copy llms.txt',
    accountRecovery: 'Account Recovery',
    accountRecoveryInfoTitle: 'What is Account Recovery?',
    accountRecoveryInfo: 'Your recovery phrase (mnemonic) is the only way to restore this wallet on another device. Tynti is self-custody: we never see or store it, and nobody can recover it for you if it’s lost. Never share it with anyone — write it down and keep it somewhere safe offline.',
    import: 'Import',
    hide: 'Hide',
    reveal: 'Reveal',
    lock: 'Lock',
    importedKeyLabel: 'Imported',
    screenshotWarning: 'Don\'t screenshot or photograph this — write it down on paper and store it somewhere safe offline.',
    verifyBackup: 'Verify backup',
    skipBackupVerify: 'Skip',
    backupVerified: 'Backup verified',
    migrateTreasury: 'Migrate treasury',
    migrateTreasuryHelp: 'If this device or your passcode may be compromised, sweep every balance on this profile to a new, trusted address.'
  },
  modals: {
    addRecipient: {
      editTitle: 'Edit recipient',
      addTitle: 'Add new recipient',
      namePlaceholder: 'Name',
      addressPlaceholder: 'Wallet address',
      saveChanges: 'Save changes',
      save: 'Save'
    },
    confirmReveal: {
      title: 'Confirm your passcode',
      help: 'Enter your passcode to reveal this recovery phrase.',
      useBiometrics: 'Use biometrics instead',
      reveal: 'Reveal',
      titleSaveRecipient: 'Confirm your passcode',
      helpSaveRecipient: 'Enter your passcode to save this recipient.',
      confirmSaveRecipient: 'Save',
      titleDeleteRecipient: 'Confirm your passcode',
      helpDeleteRecipient: 'Enter your passcode to remove this recipient.',
      confirmDeleteRecipient: 'Remove',
      titleMoveLimitOverride: 'Confirm your passcode',
      helpMoveLimitOverride: 'This send exceeds the account\'s send limit. Enter your passcode to override it for this transfer.',
      confirmMoveLimitOverride: 'Override',
      titleNewRecipientOverride: 'Confirm your passcode',
      helpNewRecipientOverride: 'This recipient is new. Enter your passcode to override the cooldown/first-payment limit for this transfer.',
      confirmNewRecipientOverride: 'Override'
    },
    importKey: {
      title: 'Import private key',
      phrasePlaceholder: 'Enter your 24-word recovery phrase',
      import: 'Import'
    },
    verifyBackup: {
      title: 'Verify your backup',
      help: 'Confirm a few words from the recovery phrase you just wrote down. You can always skip this.',
      wordLabel: 'Word #{n}',
      skip: 'Skip for now',
      confirm: 'Verify',
      error: 'One or more words don\'t match — check your written copy and try again, or skip for now.'
    },
    license: {
      title: 'MIT License'
    },
    mainnetConfirm: {
      badge: 'MAINNET',
      title: 'Switch to Mainnet?',
      body: 'Mainnet uses real funds on real networks. Balances, transaction history, and any transfers you make will no longer be test data — they will be real and, once sent, irreversible.',
      switchToMainnet: 'Switch to Mainnet'
    },
    migrateTreasury: {
      title: 'Migrate treasury',
      intro: 'Move every balance on this profile to a new address in one guided sweep. Use this if this device or your passcode may be compromised.',
      destinationLabel: 'Destination address ({chain})',
      invalidAddress: 'Enter a valid address for this network',
      noBalances: 'No balances to migrate.',
      continueReview: 'Review sweep',
      reviewTitle: 'Review sweep',
      reviewIntro: 'Every balance below will be sent in full to the destination address you entered. Check every leg before confirming — this signs {count} real transfer(s).',
      legAmount: '{amount} {ccy} from {account}',
      feeLoading: 'Quoting…',
      feeUnavailable: 'Fee unavailable — this leg will be skipped',
      gasChecking: 'Checking gas…',
      gasInsufficient: 'Insufficient gas on this account — this leg will be skipped',
      confirmSweep: 'Confirm sweep',
      sweeping: 'Sweeping…',
      statusPending: 'Pending',
      statusSending: 'Sending…',
      statusDone: 'Sent',
      statusFailed: 'Failed',
      statusSkipped: 'Skipped',
      mainnetAckLabel: 'I understand this sends real funds on Mainnet.',
      summary: 'Swept {done} of {total}.'
    },
    openAccount: {
      title: 'Open account',
      namePlaceholder: 'Account name',
      ownerPlaceholder: 'Owner (optional)',
      open: 'Open'
    },
    profileMenu: {
      title: 'Switch profile',
      addProfile: 'Add profile'
    },
    status: {
      title: 'Status',
      online: 'Online',
      offline: 'Offline',
      secure: 'Secure',
      notSecure: 'Not secure',
      checking: 'Checking…',
      connected: 'Connected',
      unreachable: 'Unreachable',
      degraded: 'Degraded',
      neverFetched: 'Never fetched',
      testnet: 'Testnet',
      mainnet: 'Mainnet',
      license: 'License',
      labelOnline: 'Online',
      labelSecure: 'Secure',
      labelBlockchains: 'Blockchains',
      labelFxRates: 'FX rates',
      labelUptime: 'Uptime',
      labelMode: 'Mode',
      labelVersion: 'Version',
      whatIsThis: 'What is this?',
      retest: 'Retest',
      explainerOnline: 'Whether this device currently has a network connection, from the browser’s own online/offline signal.',
      explainerSecure: 'Whether this page was loaded over a secure HTTPS connection (or localhost during development). A plain-HTTP load could be tampered with in transit.',
      explainerBlockchains: 'A live check that the Ethereum and Tron networks this wallet talks to are actually reachable right now — not just that the in-page wallet worker responded.',
      explainerFxRates: 'When exchange rates were last fetched. Rates older than 24 hours are flagged, though the last-known rate is still used until a fresh one arrives.',
      explainerUptime: 'A hosting reliability figure for this app’s edge hosting. There is no backend server to monitor — this app has none — so this is not a live incident feed.',
      explainerMode: 'Testnet uses free, valueless test tokens for trying the app safely. Mainnet moves real funds.',
      explainerVersion: 'The build date and commit this app is currently running, and whether a newer version is ready to install.'
    }
  },
  toast: {
    copyFailed: 'Copy failed',
    copied: '{label} copied · clipboard clears in 30s',
    accountOpened: 'Account opened',
    accountOpenedBiometric: 'Account opened — biometric unlock enabled',
    enterAccountName: 'Enter an account name',
    accountRenamed: 'Account renamed',
    ownerUpdated: 'Owner updated',
    recipientRemoved: 'Recipient removed',
    recipientUpdated: 'Recipient updated',
    csvDownloaded: 'CSV downloaded',
    pdfDownloaded: 'PDF downloaded',
    keyImported: 'Key imported',
    lockWarning: 'Locking in 30s — tap anywhere to stay unlocked',
    couldntLoadKeys: "Couldn't load keys — try again",
    couldntRevealKey: "Couldn't reveal key — try again",
    sendLimitUpdated: 'Send limit updated',
    backupVerified: 'Backup verified ✓'
  },
  errors: {
    validEmail: 'Enter a valid email address',
    nameEmpty: 'Name cannot be empty',
    validAmount: 'Enter a valid amount',
    exceedsBalance: 'Exceeds available balance',
    // Gasless (GasFree/ERC-4337 paymaster) fees are pulled from the same
    // token via a separate postOp transferFrom after the main transfer
    // executes -- sending the entire balance leaves nothing for that
    // pull. See validateMove's comment in actions.js for the full story
    // (caught live 2026-08-12, a real 20.00 USDC send against a 20.00
    // balance reverted on-chain with a raw "TransferFromFailed()").
    gaslessNeedsHeadroom: 'Leave a small amount for the network fee — this currency can\'t send its entire balance in one gasless transfer',
    chooseDestination: 'Choose a destination',
    validWalletAddress: 'Enter a valid wallet address',
    recipientWrongChain: 'This recipient\'s address isn\'t valid on {chain} — choose a different recipient or currency',
    // Currency-conversion "deliver as" (feature-swap.md Phase 4's
    // validateMove chain gate) — {chain} is the resolved settlement chain
    // (e.g. "Tron · TRC-20"), never Ethereum, since the gate only fires
    // when it isn't.
    swapUnavailableForChain: 'Currency conversion isn\'t available on {chain} — choose "Same as sent" or a different source currency',
    overSendLimit: 'This exceeds this account\'s send limit of {limit} — re-authenticate to override',
    newRecipientCooldown: 'New recipient — available to send to in about {hours}h, or re-authenticate to override',
    newRecipientEchoLimit: 'First payment to a new recipient is limited to {limit} until it\'s verified — send a smaller test amount, or re-authenticate to override',
    enterName: 'Enter a name',
    passcodeDigits: 'Passcode must be 6 digits',
    passcodesNoMatch: 'Passcodes do not match',
    enterNameOrCompany: 'Enter your name or company name',
    enter6DigitPasscode: 'Enter a 6-digit passcode',
    agreeBothStatements: 'Agree to both statements to continue',
    validRecoveryPhrase: 'Enter a valid recovery phrase',
    notBip39Phrase: 'This isn\'t a valid recovery phrase — check each word and try again',
    incorrectPasscode: 'Incorrect passcode',
    noAccountFound: 'No account found on this device',
    biometricFailed: 'Biometric check failed — enter your passcode',
    tooManyAttempts: 'Too many attempts. Try again in {seconds}s.',
    profileWiped: 'Too many failed attempts — this account\'s local data has been wiped.',
    passcodeSetupFailed: "Couldn't set up your passcode — try again",
    importFailed: 'Import failed',
    generateAddressFailed: "Couldn't generate address — try again"
  },
  csv: {
    colDate: 'Date',
    colType: 'Type',
    colDescription: 'Description',
    colAccount: 'Account',
    colCurrency: 'Currency',
    colAmount: 'Amount',
    colHash: 'Tx Hash'
  }
};
